import clsx from 'clsx';
import { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';

type Props = {
  icon: string;
  title: ReactNode;
  editing?: boolean;
  onToggleEdit?: () => void;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
};

/** Card with the page's header: icon, title, optional actions and "Editar". */
const QuickSaleCard: FC<Props> = ({
  icon,
  title,
  editing,
  onToggleEdit,
  actions,
  className,
  children,
}) => {
  const intl = useIntl();

  return (
    <div className={clsx('card mb-5', className)}>
      <div className="card-header border-0 pt-5 min-h-auto">
        <h3 className="card-title align-items-center fw-bolder fs-3">
          <i className={clsx('fas fs-2 me-3 text-gray-800', icon)} />
          {title}
        </h3>
        <div className="card-toolbar gap-2">
          {actions}
          {onToggleEdit && (
            <button
              type="button"
              className={clsx(
                'btn btn-sm',
                editing ? 'btn-primary' : 'btn-light'
              )}
              onClick={onToggleEdit}
            >
              <i className="fas fa-pen fs-7 me-1" />
              {intl.formatMessage({
                id: editing ? 'quickSale.done' : 'quickSale.edit',
              })}
            </button>
          )}
        </div>
      </div>
      <div className="card-body pt-4">{children}</div>
    </div>
  );
};

/** One label/value row; ``changed`` marks a value the draft modified. */
const FieldRow: FC<{
  label: ReactNode;
  changed?: boolean;
  children: ReactNode;
}> = ({ label, changed, children }) => (
  <div className="d-flex align-items-center py-3 border-bottom border-gray-200">
    <div className="text-gray-600 w-125px flex-shrink-0">{label}</div>
    <div
      className={clsx(
        'flex-grow-1 fw-bold text-gray-800 min-w-0',
        changed && 'text-primary'
      )}
    >
      {children}
    </div>
  </div>
);

export { QuickSaleCard, FieldRow };
